/*  Colega Link · Bloque reutilizable de Transferencia Manual
    - Muestra los datos bancarios oficiales (variables BANK_*).
    - Obliga al usuario a subir un comprobante (imagen/PDF, ≤5MB).
    - Al confirmar, invoca `onSubmit({ file_b64, file_name, mime_type })`.
    - Se usa en los modales de Pago Inicial / Sesión / Cierre de Mediación
      y en la Pago Protegido de Delegaciones.
*/
import { useState } from "react";
import { useConfig } from "@/contexts/ConfigContext";
import { Copy, Check, Upload, ShieldCheck } from "lucide-react";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

export default function ManualTransferForm({ amount, referenceLabel, submitLabel, onCancel, onSubmit, disabled, kind = "mediation" }) {
  const { config } = useConfig();
  const bank = config?.bank_account || {};
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [globalErr, setGlobalErr] = useState("");

  const plainText = [
    `Transferencia a ColegaLink · ${referenceLabel || (kind === "delegation" ? "Delegación" : "Mediación")}`,
    `Monto: ${CLP(amount)}`,
    "",
    `Banco: ${bank.bank_name || "—"}`,
    `Titular: ${bank.holder || "—"}`,
    `RUT: ${bank.holder_rut || "—"}`,
    `${bank.account_type || "Cuenta"}: ${bank.account_number || "—"}`,
    `Email para comprobante: ${bank.email || "—"}`,
  ].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    } catch { /* noop */ }
  };

  const handleFile = (f) => {
    setFileErr("");
    if (!f) { setFile(null); return; }
    if (f.size > 5 * 1024 * 1024) { setFileErr("El archivo supera los 5 MB."); return; }
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) { setFileErr("Solo se aceptan imágenes (JPG/PNG) o PDF."); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { setFileErr("Adjunta el comprobante de transferencia."); return; }
    if (!ack) return;
    setSubmitting(true); setGlobalErr("");
    try {
      const b64 = await new Promise((ok, ko) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result || "").split(",").pop());
        r.onerror = ko;
        r.readAsDataURL(file);
      });
      await onSubmit({ file_b64: b64, file_name: file.name, mime_type: file.type || "application/octet-stream" });
    } catch (e) {
      setGlobalErr(e?.response?.data?.detail || e.message || "No se pudo enviar el comprobante.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="manual-transfer-form">
      <div className="p-4 rounded-lg border border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-amber-900/20">
        <div className="text-[10px] label-eyebrow text-amber-300">Monto a transferir</div>
        <div className="mt-1 serif text-3xl text-emerald-300" data-testid="mt-amount">{CLP(amount)}</div>
      </div>

      <div className="p-3 rounded-md bg-white/5 border border-white/10 text-xs space-y-1 font-mono" data-testid="mt-bank-info">
        <BankRow label="Banco" value={bank.bank_name} />
        <BankRow label="Titular" value={bank.holder} />
        <BankRow label="RUT" value={bank.holder_rut} />
        <BankRow label={bank.account_type || "Cuenta"} value={bank.account_number} />
        <BankRow label="Email comprobante" value={bank.email} />
      </div>

      <button type="button" onClick={copy} className="w-full px-3 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2" data-testid="mt-copy">
        {copied ? <><Check size={14} className="text-emerald-300"/> Copiado</> : <><Copy size={14}/> Copiar datos bancarios</>}
      </button>

      <div className="p-3 rounded-md border border-dashed border-cyan-400/40 bg-cyan-400/[0.03]">
        <div className="text-xs label-eyebrow text-cyan-300 inline-flex items-center gap-1"><Upload size={11}/> Comprobante de transferencia (obligatorio)</div>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="mt-2 block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-white/10 file:text-foreground hover:file:bg-white/15 cursor-pointer"
          data-testid="mt-file-input"
        />
        {file && !fileErr && (
          <div className="mt-2 text-[11px] text-emerald-300 inline-flex items-center gap-1" data-testid="mt-file-ok">
            <Check size={11}/> {file.name} listo para enviar
          </div>
        )}
        {fileErr && <div className="mt-2 text-xs text-red-300" data-testid="mt-file-err">{fileErr}</div>}
      </div>

      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="mt-ack"/>
        <span>Realicé la transferencia con estos datos y adjunto el comprobante. Entiendo que el pago queda en <strong>Pendiente de validación</strong> hasta que administración lo apruebe manualmente.</span>
      </label>

      <div className="p-3 rounded-md border border-emerald-400/25 bg-emerald-400/[0.06] text-xs text-emerald-200 inline-flex items-start gap-2">
        <ShieldCheck size={13} className="mt-0.5 shrink-0"/>
        <span>Tu comprobante llega al administrador de forma privada. Recibirás una notificación en la plataforma cuando sea validado.</span>
      </div>

      {globalErr && <div className="text-xs text-red-300" data-testid="mt-global-err">{globalErr}</div>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!file || !ack || submitting || disabled}
          className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mt-submit"
        >
          {submitting ? "Enviando…" : (submitLabel || "Enviar comprobante")}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-ghost" data-testid="mt-cancel">Cancelar</button>
        )}
      </div>
    </div>
  );
}

function BankRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground font-medium text-right">{value || "—"}</span>
    </div>
  );
}
